import i18next from "i18next"
import { User, UserData, ErrorFn, ErrorResponse } from "models"
import { http, catchError, getError } from "services/http"

/***************************
 **** GET USER / ACCOUNT ****
 **************************/

export type GetUserAnswer = {
	account_id: string
	description: UserData
	operator_id: string
	create_date: string
	modify_date: string
}

export const getUser = async (): Promise<{ res: GetUserAnswer } & ErrorFn> => {
	const userErrors = (error: ErrorResponse) => {
		switch (error?.code) {
			case "NotAuthorizedException":
				return {
					code: error?.code,
					message: i18next.t("error_not_authorized", { ns: "form" }),
				}
			case "RessourceNotFoundException":
				return {
					code: error?.code,
					message: i18next.t("error_ressource_not_found", { ns: "form" }),
				}
			default:
				return {
					code: error?.code,
					message: i18next.t("error_default", { ns: "form" }),
				}
		}
	}

	const res = await http
		.get<GetUserAnswer>("obikeapi/account/get_account")
		.then((res) => res.data)
		.catch((e) => catchError(e))

	return {
		res: res as GetUserAnswer,
		getError: "error" in res ? getError(res.error, userErrors) : null,
	}
}

/***************************
 **** UPDATE USER / ACCOUNT ****
 **************************/

export type UpdateUserAnswer = {
	account_id: string
	description: UserData
	operator_id: string
	row_version: string
}

export type UserUpdateEmailProps = {
	code: string
	account_id: string
	row_version: string
	mail: string
}

export const updateUser = async (
	newUserData: User | UserUpdateEmailProps // Update all user's data or just email
): Promise<{ res: UpdateUserAnswer } & ErrorFn> => {
	const updateUserErrors = (error: ErrorResponse) => {
		switch (error?.code) {
			case "NotAuthorizedException":
				switch (error?.message) {
					case "Code expired or invalid.":
						return {
							code: error?.code,
							message: i18next.t("expired_code", { ns: "form" }),
						}

					default:
						return {
							code: error?.code,
							message: i18next.t("error_not_authorized", { ns: "form" }),
						}
				}
			case "AccountVersionMismatchException":
				return {
					code: error?.code,
					message: i18next.t("old_version", { ns: "form" }),
				}
			case "TooManyRequestException":
				return {
					code: error?.code,
					message: i18next.t("already_update", { ns: "form" }),
				}
			case "InvalidArgument":
				return {
					code: error?.code,
					message: i18next.t("error_invalid_value", { ns: "form" }),
				}
			default:
				return {
					code: error?.code,
					message: i18next.t("error_default", { ns: "form" }),
				}
		}
	}

	const res = await http
		.put<UpdateUserAnswer>("obikeapi/account/update_account", newUserData)
		.then((res) => res.data)
		.catch((e) => catchError(e))
	return {
		res: res as UpdateUserAnswer,
		getError: getError(res as unknown as ErrorResponse, updateUserErrors),
	}
}

/***************************
 ***** UPDATE PASSWORD *****
 **************************/

export type UpdatePasswordAnswer = Record<string, never>

export interface UpdatePasswordProps {
	row_version: string
	account_id: string
	old_password: string
	new_password: string
}

export const updatePassword = async (props: UpdatePasswordProps): Promise<ErrorFn> => {
	const updateUserErrors = (error: ErrorResponse) => {
		switch (error?.code) {
			case "NotAuthorizedException":
				return {
					code: error?.code,
					message: i18next.t("error_actual_mail", { ns: "form" }),
				}
			case "AccountVersionMismatchException":
				return {
					code: error?.code,
					message: i18next.t("old_version", { ns: "form" }),
				}
			case "InvalidArgument":
				return {
					code: error?.code,
					message: i18next.t("error_invalid_value", { ns: "form" }),
				}
			default:
				return {
					code: error?.code,
					message: i18next.t("error_default", { ns: "form" }),
				}
		}
	}

	const res = await http
		.put<UpdatePasswordAnswer>("obikeapi/account/update_account_password", props)
		.then((res) => res.data)
		.catch((e) => catchError(e))
	return {
		getError: getError(res as unknown as ErrorResponse, updateUserErrors),
	}
}

/***************************
 ***** DELETE ACCOUNT *****
 **************************/

export type DeleteAccountAnswer = Record<string, never>

export const deleteAccount = async (accountId: string, rowVersion: string): Promise<ErrorFn> => {
	const deleteAccountErrors = (error: ErrorResponse) => {
		switch (error?.code) {
			case "NotAuthorizedException":
				switch (error?.message) {
					case "Credentials are required.":
						return {
							code: error?.code,
							message: i18next.t("expired_session", { ns: "form" }),
						}

					default:
						return {
							code: error?.code,
							message: i18next.t("bikes_in_dashboard_delete_account_error", {
								ns: "form",
							}),
						}
				}
			case "AccountVersionMismatchException":
				return {
					code: error?.code,
					message: i18next.t("old_version", { ns: "form" }),
				}
			default:
				return {
					code: error?.code,
					message: i18next.t("error_default", { ns: "form" }),
				}
		}
	}

	const res = await http
		.delete<DeleteAccountAnswer>(
			`obikeapi/account/delete_account?account_id=${accountId}&row_version=${rowVersion}`
		)
		.then((res) => res.data)
		.catch((e) => catchError(e))
	return {
		getError: getError(res as unknown as ErrorResponse, deleteAccountErrors),
	}
}
